import api from './api';

/**
 * Data service for fetching user data from social platforms
 */
export const dataService = {
  /**
   * Fetch Reddit data for current user
   * This will be called after OAuth authentication
   * @param {object} options - Fetch options (limit, subreddits, etc.)
   * @returns {Promise}
   */
  fetchRedditData: async (options = {}) => {
    try {
      const response = await api.post('/data/reddit/fetch/', options);
      return response.data;
    } catch (error) {
      console.error('Fetch Reddit data error:', error);
      throw error;
    }
  },

  /**
   * Start scan for all connected platforms
   * @param {Array} platforms - Array of platform names ['reddit', 'facebook', 'youtube']
   * @returns {Promise}
   */
  startScan: async (platforms = ['reddit']) => {
    try {
      const response = await api.post('/data/scan/start/', { platforms });
      return response.data;
    } catch (error) {
      console.error('Start scan error:', error);
      throw error;
    }
  },

  /**
   * Check scan status
   * @param {string} jobId - Scan job ID
   * @returns {Promise}
   */
  getScanStatus: async (jobId) => {
    try {
      const response = await api.get(`/data/scan/status/${jobId}/`);
      return response.data;
    } catch (error) {
      console.error('Get scan status error:', error);
      throw error;
    }
  },

  /**
   * Upload a Facebook ad insights JSON file (advertisers or off-Facebook activity).
   * @param {File} file - The JSON data export file
   * @param {string} fileType - 'advertisers' | 'off_facebook'
   * @param {string} username - Facebook display name
   * @returns {Promise}
   */
  uploadAdInsightsFile: async (file, fileType, username) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('platform', 'facebook');
      if (username) formData.append('username', username);
      const response = await api.post('/privacy/upload_insights/', formData, {
        timeout: 300000,
      });
      return response.data;
    } catch (error) {
      console.error('Ad insights upload error:', error);
      throw error;
    }
  },

  /**
   * Upload a Facebook 'Download Your Information' JSON file.
   * @param {File} file - The JSON data export file
   * @param {string} fileType - 'posts' | 'comments' (default: 'posts')
   * @param {string} username - Optional username for context
   * @returns {Promise}
   */
  uploadFacebookFile: async (file, fileType = 'posts', username = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      if (username) formData.append('username', username);
      const response = await api.post('/data/facebook/upload/', formData, {
        timeout: 600000,
      });
      return response.data;
    } catch (error) {
      console.error('Facebook upload error:', error);
      throw error;
    }
  },

  /**
   * Trigger a live Facebook data scan using the stored OAuth token.
   * @param {string} username - Optional username for context
   * @returns {Promise}
   */
  scanFacebook: async (username = null) => {
    try {
      const params = {};
      if (username) params.username = username;
      const response = await api.get('/data/facebook/scan/', { params });
      return response.data;
    } catch (error) {
      console.error('Facebook scan error:', error);
      throw error;
    }
  },

  /**
   * Re-run privacy detection on all Facebook posts/comments already stored in MongoDB
   * (no Graph API). Populates privacy_findings for dashboard/exposures.
   */
  reanalyzeFacebookFromStored: async (username = null) => {
    try {
      const params = { from_db_only: '1' };
      if (username) params.username = username;
      const response = await api.get('/data/facebook/scan/', { params });
      return response.data;
    } catch (error) {
      console.error('Facebook reanalyze from DB error:', error);
      throw error;
    }
  },

  /**
   * Get platform data (posts, comments)
   * @param {string} platform - Platform name
   * @returns {Promise}
   */
  getPlatformData: async (platform) => {
    try {
      const response = await api.get(`/data/${platform}/`);
      return response.data;
    } catch (error) {
      console.error('Get platform data error:', error);
      throw error;
    }
  },

  /**
   * Get all posts for current user
   * @param {object} filters - Filter options
   * @returns {Promise}
   */
  getPosts: async (filters = {}) => {
    try {
      const response = await api.get('/data/posts/', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Get posts error:', error);
      throw error;
    }
  },

  /**
   * Get all comments for current user
   * @param {object} filters - Filter options
   * @returns {Promise}
   */
  getComments: async (filters = {}) => {
    try {
      const response = await api.get('/data/comments/', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Get comments error:', error);
      throw error;
    }
  },

  /**
   * Scan for new data and analyze for privacy exposures
   * This fetches new Reddit posts/comments and immediately analyzes them
   * @param {string} username - Reddit username
   * @param {object} options - Scan options (limit_posts, limit_comments, platform)
   * @returns {Promise}
   */
  scanAndAnalyze: async (username, options = {}) => {
    try {
      const response = await api.post('/data/scan-and-analyze/', {
        username,
        platform: options.platform || 'reddit',
        limit_posts: options.limit_posts || 100,
        limit_comments: options.limit_comments || 100
      });
      return response.data;
    } catch (error) {
      console.error('Scan and analyze error:', error);
      throw error;
    }
  },

  /**
   * Upload a Google Takeout ZIP (YouTube comments export) and run privacy analysis.
   * @param {File} file - Takeout ZIP file (e.g. Takeout-xxx.zip)
   * @returns {Promise}
   */
  uploadYouTubeTakeout: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/data/upload-youtube-takeout/', formData, {
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('YouTube Takeout upload error:', error);
      throw error;
    }
  },
};

export default dataService;
